package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero ----------

val PricingHeroStyle = CssStyle.base {
    Modifier.css("padding-block" to "clamp(40px, 6vw, 72px) clamp(28px, 4vw, 40px)")
}

/** The price builder sits just below the hero, with room before step 1. */
val PricingBuilderSectionStyle = CssStyle.base {
    Modifier.css("padding-top" to "clamp(24px, 4vw, 48px)")
}

/** Two boxes that keep the website fee and the care plan clearly separate. */
val PricingSplitFactsStyle = CssStyle {
    base { grid("1fr").gap(14.px).css("max-width" to "820px", "margin-top" to "6px") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
    cssRule(" > div") {
        Modifier.display(DisplayStyle.Grid).gap(8.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(12.px).css("align-content" to "start", "padding" to "16px 18px", "border-left" to "4px solid rgb(180,198,220)")
    }
    cssRule(" > div + div") { Modifier.css("border-left-color" to "rgb(46,75,110)") }
    cssRule(" p:last-child") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
    cssRule(" b") { Modifier.color(RdColor.Ink).css("font-weight" to 600) }
}

val PricingFactLabelStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

// ---------- Quote builder ----------

val PricingBuilderGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "clamp(28px, 4vw, 56px)", "align-items" to "start") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "minmax(0, 1fr) 360px") }
}

val PricingStepsStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(44.px).css("min-width" to "0")
}

/** Step headings are a size down from section headings. */
val PricingStepTitleStyle = CssStyle.base {
    Modifier.css("font-size" to "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem) !important")
}

val PricingFieldsetStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(16.px).css("border" to "0", "margin" to "0", "padding" to "0", "min-width" to "0") }
    cssRule(" legend") { Modifier.display(DisplayStyle.Grid).gap(6.px).css("padding" to "0", "margin-bottom" to "16px") }
    cssRule(" > p") { Modifier.color(RdColor.Muted).css("margin-top" to "-6px") }
}

val PricingOptionsTwoStyle = CssStyle {
    // Room for the badge that sits over the top edge of a card when the cards are stacked.
    base { grid("1fr").gap(24.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val PricingOptionsThreeStyle = CssStyle {
    // Room for the badge that sits over the top edge of a card when the cards are stacked.
    base { grid("1fr").gap(24.px) }
    Breakpoint.XL { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
}

/** A selectable card wrapping a visually hidden radio button. */
val PricingOptionStyle = CssStyle {
    base {
        Modifier.position(Position.Relative).display(DisplayStyle.Grid).gap(12.px).backgroundColor(RdColor.Paper)
            .border(1.5.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px).cursor(Cursor.Pointer)
            .css("align-content" to "start", "padding" to "22px", "min-width" to "0", "transition" to "border-color .2s, box-shadow .2s")
    }
    hover { Modifier.css("border-color" to "rgb(180,198,220)") }
    cssRule(" input") { Modifier.position(Position.Absolute).css("opacity" to "0", "pointer-events" to "none", "width" to "1px", "height" to "1px") }
    cssRule(":focus-within") { Modifier.css("outline" to "3px solid rgb(180,198,220)", "outline-offset" to "2px") }
    cssRule(" > p") { Modifier.color(RdColor.Muted).css("font-size" to ".93rem") }
    cssRule(" > a") { Modifier.color(RdColor.Brand).css("font-size" to ".88rem", "font-weight" to 600) }
}

val PricingOptionSelectedStyle = CssStyle {
    base { Modifier.css("border-color" to "rgb(46,75,110)", "box-shadow" to "0 0 0 3px rgb(230,237,245)") }
    cssRule(" .pricing-radio") { Modifier.css("border-color" to "rgb(46,75,110)") }
    cssRule(" .pricing-radio::after") {
        Modifier.backgroundColor(RdColor.Brand).css("content" to "\"\"", "width" to "10px", "height" to "10px", "border-radius" to "50%")
    }
}

val PricingOptionHeadStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.FlexStart).gap(10.px) }
    cssRule(" h3") { Modifier.css("font-size" to "1.15rem") }
}

/** The round radio indicator drawn in the card's corner. */
val PricingRadioStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).borderRadius(50.px)
        .css("flex" to "none", "width" to "22px", "height" to "22px", "place-items" to "center", "border" to "2px solid rgb(220,227,236)", "transition" to "border-color .2s")
}

val PricingPriceStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Baseline).gap(6.px).fontFamily(*RdFont.DISPLAY)
            .css("flex-wrap" to "wrap", "font-size" to "2.2rem", "font-weight" to 900, "font-stretch" to "112%", "line-height" to 1, "font-variant-numeric" to "tabular-nums")
    }
    cssRule(" small") { Modifier.color(RdColor.Muted).css("font" to "500 .85rem/1.2 ${RdFont.BODY_STACK}") }
}

/** The small "from" before a starting price. */
val PricingFromStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("font" to "500 .85rem/1 ${RdFont.BODY_STACK}", "align-self" to "center")
}

val PricingFeaturesStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(7.px).css("list-style" to "none", "padding" to "0", "font-size" to ".9rem") }
    cssRule(" li") { Modifier.display(DisplayStyle.Grid).gap(8.px).color(RdColor.Ink).css("grid-template-columns" to "16px 1fr") }
    cssRule(" li::before") { Modifier.color(RdColor.Brand).css("content" to "\"✓\"", "font-weight" to 700) }
}

/** "Everything in the plan below" line, marked with + instead of a tick. */
val PricingPlusStyle = CssStyle {
    // !important: the feature list sets item colour with a more specific selector.
    base { Modifier.css("color" to "rgb(95,99,104) !important", "font-style" to "italic") }
    cssRule("::before") { Modifier.css("content" to "\"+\" !important") }
}

val PricingBadgeStyle = CssStyle.base {
    Modifier.position(Position.Absolute).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(999.px)
        .css("top" to "-11px", "left" to "18px", "padding" to "6px 9px", "font" to "500 .66rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
}

val PricingBadgeSoftStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.LogoBlue).color(RdColor.Ink)
}

// ---------- Quote sheet ----------

/** A trade-style quote that stays in view while options are chosen. */
val PricingQuoteStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px)
            .css("overflow" to "hidden", "max-width" to "520px", "box-shadow" to RdSize.SHADOW)
    }
    Breakpoint.LG { Modifier.position(Position.Sticky).css("top" to "calc(env(safe-area-inset-top, 0px) + 92px)", "max-width" to "none") }
}

val PricingQuoteHeadStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.Center)
            .backgroundColor(RdColor.Band).color(RdColor.BandText).css("padding" to "18px 22px")
    }
    cssRule(" b") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "1.2rem", "font-weight" to 900, "font-stretch" to "125%", "letter-spacing" to ".06em") }
    cssRule(" span") { Modifier.color(RdColor.BandMuted).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".06em") }
}

val PricingQuoteBodyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(14.px).css("padding" to "18px 22px 22px") }
    cssRule(" > a") { Modifier.css("justify-content" to "center") }
}

val PricingQuoteLinesStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).css("list-style" to "none", "padding" to "0") }
    cssRule(" li") { Modifier.display(DisplayStyle.Grid).css("grid-template-columns" to "1fr auto", "gap" to "2px 16px", "padding" to "12px 0", "border-bottom" to "1px dashed rgb(220,227,236)", "font-size" to ".93rem") }
    cssRule(" li > span:first-child") { Modifier.css("font-weight" to 600) }
    cssRule(" li > span:nth-child(2)") { Modifier.css("font-weight" to 600, "text-align" to "right", "font-variant-numeric" to "tabular-nums") }
    cssRule(" small") { Modifier.color(RdColor.Muted).css("grid-column" to "1 / -1", "font-size" to ".8rem") }
}

val PricingQuoteFreeStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font-weight" to "700 !important")
}

val PricingQuoteTotalStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.Baseline).gap(12.px).css("padding-top" to "4px") }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font" to "500 .74rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase") }
    cssRule(" b") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "2rem", "font-weight" to 900, "font-stretch" to "112%", "line-height" to 1, "font-variant-numeric" to "tabular-nums") }
}

val PricingQuoteNoteStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).backgroundColor(RdColor.SkySoft).border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(10.px)
        .css("font-size" to ".88rem", "padding" to "12px 14px")
}

// ---------- Comparison table ----------

val PricingTableWrapStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px).css("overflow-x" to "auto")
}

val PricingTableStyle = CssStyle {
    // On phones the table scrolls sideways with the feature column fixed: about 150px for features and at least
    // 112px per plan, so a plan and a half stay in view next to it.
    base { Modifier.css("width" to "100%", "border-collapse" to "collapse", "min-width" to "486px", "font-size" to ".95rem") }
    cssRule(" th") { Modifier.css("padding" to "14px 18px", "text-align" to "left", "border-bottom" to "1px solid rgb(220,227,236)") }
    cssRule(" td") {
        Modifier.css("padding" to "14px 18px", "text-align" to "center", "border-bottom" to "1px solid rgb(220,227,236)", "width" to "max(18%, 112px)", "font-variant-numeric" to "tabular-nums")
    }
    cssRule(" thead th") { Modifier.backgroundColor(RdColor.Ground).fontFamily(*RdFont.DISPLAY).css("font-size" to "1rem", "font-weight" to 800, "font-stretch" to "108%", "line-height" to 1.2, "vertical-align" to "bottom") }
    // Plan names and prices line up with the ticks below them.
    cssRule(" thead th + th") { Modifier.css("text-align" to "center") }
    cssRule(" thead small") { Modifier.color(RdColor.Muted).css("display" to "block", "font" to "500 .82rem/1.4 ${RdFont.BODY_STACK}", "margin-top" to "4px") }
    // Opaque, so the plan columns slide under the fixed feature column on phones.
    cssRule(" tbody th") { Modifier.color(RdColor.Ink).css("font-weight" to 500) }
    cssRule(" tbody th:not([colspan])") { Modifier.backgroundColor(RdColor.Paper) }
    cssRule(" tr > :last-child") { Modifier.css("background" to "rgba(230,237,245,.55)") }
    cssRule(" thead tr > :last-child") { Modifier.backgroundColor(RdColor.SkySoft) }
    cssRule(" tbody tr:last-child > *") { Modifier.css("border-bottom" to "0") }
}

val PricingTableGroupStyle = CssStyle {
    cssRule(" th") {
        Modifier.color(RdColor.Brand).backgroundColor(RdColor.Ground)
            .css("padding-block" to "10px", "font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
    }
    // Stays in view at the left while the row is swiped sideways on phones.
    cssRule(" th > span") { Modifier.position(Position.Sticky).css("left" to "18px", "display" to "inline-block") }
}

val PricingTickStyle = CssStyle.base { Modifier.color(RdColor.Brand).css("font-weight" to 700) }
val PricingDashStyle = CssStyle.base { Modifier.color(RdColor.Line).css("font-weight" to 700) }

val PricingTableNoteStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("margin-top" to "14px", "font-size" to ".92rem")
}

// ---------- Included ----------

/** White band with hairlines, so "Every website includes" stands apart from the pale-blue FAQ band after it. */
val PricingIncludedBandStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Paper).css("border-block" to "1px solid rgb(220,227,236)")
}

val PricingIncludedGridStyle = CssStyle {
    base { grid("1fr").gap(20.px) }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(4, 1fr)") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(8.px).css("padding-top" to "18px", "border-top" to "2px solid rgb(180,198,220)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
}

// ---------- Basic plan update prices (info button in the comparison table) ----------

val PricingInfoCellStyle = CssStyle.base {
    Modifier.css("display" to "inline-flex", "align-items" to "center", "gap" to "4px")
}

val PricingInfoButtonStyle = CssStyle {
    base {
        Modifier.color(RdColor.Brand).css(
            "display" to "inline-grid", "place-items" to "center", "width" to "30px", "height" to "30px", "margin" to "-6px -6px -6px 0",
            "padding" to "0", "border" to "0", "border-radius" to "50%", "background" to "transparent", "cursor" to "pointer",
            // Never squashed into an oval, and no square tap flash on phones: the round background shows instead.
            "flex" to "none", "aspect-ratio" to "1", "-webkit-tap-highlight-color" to "transparent"
        )
    }
    hover { Modifier.backgroundColor(RdColor.SkySoft) }
    cssRule(":active") { Modifier.backgroundColor(RdColor.SkySoft) }
    cssRule("[aria-expanded=\"true\"]") { Modifier.backgroundColor(RdColor.SkySoft) }
    cssRule(":focus-visible") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "outline-offset" to "1px") }
}

/**
 * The price list itself: shown in the browser's popover layer and placed by script, under the info button
 * with a mouse, or centred over a dimmed page on touch screens. Pale blue so it stands apart from the page.
 */
val PricingUpdatePricesStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.SkySoft).color(RdColor.Ink).css(
            // Hidden unless open, also in browsers that don't know the popover attribute.
            "display" to "none",
            "position" to "fixed", "inset" to "auto", "margin" to "0",
            "box-sizing" to "border-box", "width" to "min(360px, calc(100vw - 32px))",
            "max-height" to "calc(100dvh - 32px)", "overflow-y" to "auto", "overscroll-behavior" to "contain",
            "padding" to "16px 20px 18px", "border" to "1px solid rgb(180,198,220)", "border-radius" to "14px",
            "box-shadow" to RdSize.SHADOW, "text-align" to "left", "font-size" to ".9rem", "line-height" to 1.45
        )
    }
    cssRule(":popover-open") { Modifier.css("display" to "grid", "gap" to "10px") }
    // Opened by tap: the page behind is dimmed.
    cssRule("[data-modal]::backdrop") { Modifier.css("background" to "rgba(30,40,52,.45)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("margin" to "0") }
    cssRule(" ul") { Modifier.css("list-style" to "none", "padding" to "0", "margin" to "0", "border-top" to "1px solid rgb(180,198,220)") }
    cssRule(" li") {
        Modifier.css(
            "display" to "flex", "justify-content" to "space-between", "align-items" to "baseline", "gap" to "16px",
            "padding" to "8px 0", "border-bottom" to "1px solid rgb(180,198,220)"
        )
    }
    cssRule(" li b") { Modifier.css("flex" to "none", "white-space" to "nowrap", "font-variant-numeric" to "tabular-nums") }
}

/** Title on the left, close button on the right. */
val PricingUpdatePricesHeadStyle = CssStyle.base {
    Modifier.css("display" to "flex", "align-items" to "center", "justify-content" to "space-between", "gap" to "12px", "font-size" to "1rem")
}

val PricingUpdatePricesCloseStyle = CssStyle {
    base {
        Modifier.color(RdColor.Ink).css(
            "flex" to "none", "display" to "grid", "place-items" to "center", "width" to "36px", "height" to "36px",
            "margin" to "-8px -10px -8px 0", "padding" to "0", "border" to "0", "border-radius" to "50%",
            "background" to "transparent", "cursor" to "pointer", "-webkit-tap-highlight-color" to "transparent"
        )
    }
    hover { Modifier.backgroundColor(RdColor.Paper) }
    cssRule(":active") { Modifier.backgroundColor(RdColor.Paper) }
    cssRule(":focus-visible") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "outline-offset" to "1px") }
}
